import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

// Connects to data-controller="multiselect"
// Transforms a <select multiple> into a button dropdown with checkboxes
export default class extends Controller {
  static values = {
    placeholder: { type: String, default: "None selected" },
    placeholderChosen: String,
    numberDisplayed: { type: Number, default: 3 },
    selectAllText: { type: String, default: "Select all" },
    nSelectedText: { type: String, default: "selected" },
    allSelectedText: { type: String, default: "All selected" },
    includeSelectAll: { type: Boolean, default: false }
  }

  connect() {
    this.select = this.element
    this.isMultiple = this.select.hasAttribute('multiple')

    // Don't transform if not a select element
    if (this.select.tagName !== 'SELECT') {
      console.error('multiselect controller must be attached to a <select> element')
      return
    }

    this.buildUI()
    this.updateButtonText()
    this.syncFromSelect()
  }

  disconnect() {
    if (this.container) {
      this.container.remove()
    }
    this.select.style.display = ''
    this.select.tabIndex = ''
  }

  buildUI() {
    // Hide the original select
    this.select.style.display = 'none'
    this.select.tabIndex = '-1'

    // Create container
    this.container = document.createElement('div')
    this.container.classList.add('btn-group', 'd-flex', 'multiselect-native-select')

    // Create button
    this.button = document.createElement('button')
    this.button.type = 'button'
    this.button.classList.add('multiselect', 'dropdown-toggle', 'form-select', 'text-start')
    this.button.setAttribute('data-bs-toggle', 'dropdown')
    this.button.setAttribute('aria-expanded', 'false')

    if (this.select.classList.contains('form-control')) {
      this.select.classList.remove('form-control')
    }

    this.buttonText = document.createElement('span')
    this.buttonText.classList.add('multiselect-selected-text')
    this.button.appendChild(this.buttonText)

    // Create dropdown menu
    this.dropdown = document.createElement('div')
    this.dropdown.classList.add('dropdown-menu', 'multiselect-container')

    // Build options
    this.buildOptions()

    // Assemble
    this.container.appendChild(this.button)
    this.container.appendChild(this.dropdown)
    this.select.parentNode.insertBefore(this.container, this.select)

    // Event listeners
    this.dropdown.addEventListener('click', (e) => this.handleDropdownClick(e))
    this.select.addEventListener('change', () => this.syncFromSelect())
  }

  buildFilter() {
    const filterDiv = document.createElement('div')
    filterDiv.classList.add('multiselect-filter')

    const searchInput = document.createElement('input')
    searchInput.type = 'search'
    searchInput.classList.add('multiselect-search', 'form-control', 'form-control-sm')
    searchInput.placeholder = 'Search'

    filterDiv.appendChild(searchInput)
    this.dropdown.appendChild(filterDiv)

    // Filter on input
    searchInput.addEventListener('input', (e) => {
      this.filterOptions(e.target.value)
    })

    // Prevent dropdown from closing when clicking in search
    searchInput.addEventListener('click', (e) => {
      e.stopPropagation()
    })
  }

  filterOptions(query) {
    const normalizedQuery = query.toLowerCase().trim()
    const allOptions = Array.from(this.dropdown.querySelectorAll('.multiselect-option, .multiselect-group'))

    if (normalizedQuery === '') {
      // Show all
      allOptions.forEach(el => {
        el.style.display = ''
        el.classList.remove('multiselect-filter-hidden')
      })
      return
    }

    let currentGroup = null
    let currentGroupHasVisibleItems = false

    allOptions.forEach(el => {
      if (el.classList.contains('multiselect-group')) {
        // This is a group header
        if (currentGroup) {
          // Update previous group visibility
          if (currentGroupHasVisibleItems) {
            currentGroup.style.display = ''
            currentGroup.classList.remove('multiselect-filter-hidden')
          } else {
            currentGroup.style.display = 'none'
            currentGroup.classList.add('multiselect-filter-hidden')
          }
        }
        currentGroup = el
        currentGroupHasVisibleItems = false
      } else {
        // This is an option
        const label = el.querySelector('.form-check-label')
        const text = label ? label.textContent.toLowerCase() : ''
        const input = el.querySelector('input')
        const value = input ? input.value.toLowerCase() : ''

        const matches = text.includes(normalizedQuery) || value.includes(normalizedQuery)

        if (matches) {
          el.style.display = ''
          el.classList.remove('multiselect-filter-hidden')
          currentGroupHasVisibleItems = true
        } else {
          el.style.display = 'none'
          el.classList.add('multiselect-filter-hidden')
        }
      }
    })

    // Handle last group
    if (currentGroup) {
      if (currentGroupHasVisibleItems) {
        currentGroup.style.display = ''
        currentGroup.classList.remove('multiselect-filter-hidden')
      } else {
        currentGroup.style.display = 'none'
        currentGroup.classList.add('multiselect-filter-hidden')
      }
    }
  }

  buildOptions() {
    this.dropdown.innerHTML = ''

    // Add search filter
    this.buildFilter()

    // Build select all if needed
    if (this.includeSelectAllValue && this.isMultiple) {
      const selectAllItem = this.createCheckboxItem(
        null,
        this.selectAllTextValue,
        'multiselect-all'
      )
      selectAllItem.querySelector('input').addEventListener('change', (e) => {
        this.handleSelectAll(e.target.checked)
      })
      this.dropdown.appendChild(selectAllItem)
    }

    // Build options from select
    Array.from(this.select.children).forEach(child => {
      if (child.tagName === 'OPTGROUP') {
        this.createOptgroup(child)
      } else if (child.tagName === 'OPTION') {
        // Skip disabled header options
        if (child.disabled && (child.classList.contains('header') || child.value === '')) {
          return
        }
        const item = this.createCheckboxItem(child, child.text)
        this.dropdown.appendChild(item)
      }
    })
  }

  createOptgroup(optgroup) {
    // Create optgroup header
    const header = document.createElement('div')
    header.classList.add('dropdown-item-text', 'multiselect-group', 'fw-bold')
    header.textContent = optgroup.label
    this.dropdown.appendChild(header)

    // Create options in the optgroup
    Array.from(optgroup.children).forEach(option => {
      // Skip disabled header options
      if (option.disabled && (option.classList.contains('header') || option.value === '')) {
        return
      }
      const item = this.createCheckboxItem(option, option.text)
      item.classList.add('multiselect-group-option-indented')
      this.dropdown.appendChild(item)
    })
  }

  createCheckboxItem(option, label, extraClass) {
    const item = document.createElement('button')
    item.type = 'button'
    item.classList.add('dropdown-item', 'multiselect-option')
    if (extraClass) {
      item.classList.add(extraClass)
    }

    const wrapper = document.createElement('span')
    wrapper.classList.add('form-check')

    const inputType = this.isMultiple ? 'checkbox' : 'radio'
    const input = document.createElement('input')
    input.classList.add('form-check-input')
    input.type = inputType

    if (option) {
      input.value = option.value
      input.checked = option.selected
      input.disabled = option.disabled
    }

    const labelElement = document.createElement('label')
    labelElement.classList.add('form-check-label')
    labelElement.textContent = label

    wrapper.appendChild(input)
    wrapper.appendChild(labelElement)
    item.appendChild(wrapper)

    return item
  }

  handleDropdownClick(e) {
    e.stopPropagation()

    // Find the checkbox/radio that was clicked
    const target = e.target
    const item = target.closest('.multiselect-option')
    if (!item) return

    const input = item.querySelector('input')
    if (!input) return

    // Toggle if not already toggled
    if (target !== input) {
      input.checked = !input.checked
    }

    const value = input.value

    if (this.isMultiple) {
      // Multiple selection
      const option = this.select.querySelector(`option[value="${value}"]`)
      if (option) {
        option.selected = input.checked
        if (input.checked) {
          item.classList.add('active')
        } else {
          item.classList.remove('active')
        }
      }
    } else {
      // Single selection - close dropdown and update
      Array.from(this.dropdown.querySelectorAll('.multiselect-option')).forEach(i => {
        i.classList.remove('active')
        const inp = i.querySelector('input')
        if (inp && inp !== input) {
          inp.checked = false
        }
      })

      Array.from(this.select.options).forEach(opt => {
        opt.selected = (opt.value === value)
      })

      item.classList.add('active')

      // Close dropdown for single select
      const bsDropdown = bootstrap.Dropdown.getInstance(this.button)
      if (bsDropdown) {
        bsDropdown.hide()
      }
    }

    // Trigger change event on the select
    this.select.dispatchEvent(new Event('change', { bubbles: true }))
    this.updateButtonText()
    this.updateSelectAll()
  }

  handleSelectAll(checked) {
    const visibleOptions = Array.from(this.dropdown.querySelectorAll('.multiselect-option:not(.multiselect-all) input'))

    visibleOptions.forEach(input => {
      if (!input.disabled) {
        input.checked = checked
        const value = input.value
        const option = this.select.querySelector(`option[value="${value}"]`)
        if (option) {
          option.selected = checked
        }

        const item = input.closest('.multiselect-option')
        if (checked) {
          item.classList.add('active')
        } else {
          item.classList.remove('active')
        }
      }
    })

    this.select.dispatchEvent(new Event('change', { bubbles: true }))
    this.updateButtonText()
  }

  syncFromSelect() {
    // Update checkboxes to match select state
    Array.from(this.select.options).forEach(option => {
      const input = this.dropdown.querySelector(`input[value="${option.value}"]`)
      if (input) {
        input.checked = option.selected
        const item = input.closest('.multiselect-option')
        if (option.selected) {
          item.classList.add('active')
        } else {
          item.classList.remove('active')
        }
      }
    })

    this.updateButtonText()
    this.updateSelectAll()
  }

  updateSelectAll() {
    if (!this.includeSelectAllValue || !this.isMultiple) return

    const allCheckboxes = Array.from(
      this.dropdown.querySelectorAll('.multiselect-option:not(.multiselect-all) input:not([disabled])')
    )
    const checkedCheckboxes = allCheckboxes.filter(input => input.checked)
    const selectAllInput = this.dropdown.querySelector('.multiselect-all input')

    if (selectAllInput) {
      selectAllInput.checked = (allCheckboxes.length > 0 && checkedCheckboxes.length === allCheckboxes.length)
    }
  }

  updateButtonText() {
    const selected = Array.from(this.select.options).filter(opt => opt.selected)
    const prefix = this.placeholderChosenValue || this.placeholderValue

    if (this.select.disabled) {
      this.buttonText.textContent = this.placeholderValue
      this.button.disabled = true
      return
    }

    this.button.disabled = false

    if (selected.length === 0) {
      this.buttonText.textContent = this.placeholderValue
    } else if (selected.length === this.select.options.length && this.isMultiple) {
      this.buttonText.textContent = this.allSelectedTextValue
    } else if (selected.length > this.numberDisplayedValue) {
      this.buttonText.textContent = `${selected.length} ${this.nSelectedTextValue}`
    } else {
      const labels = selected.map(opt => opt.text)
      let text = labels.join(', ')
      if (this.placeholderChosenValue && selected.length > 0) {
        text = `${this.placeholderChosenValue}: ${text}`
      }
      this.buttonText.textContent = text
    }

    this.button.title = selected.map(opt => opt.text).join(', ') || this.placeholderValue
  }

  // Public method for form reset
  refresh() {
    this.syncFromSelect()
  }

  // Public method to rebuild (if options change dynamically)
  rebuild() {
    this.buildOptions()
    this.syncFromSelect()
  }
}
